import type { JSX, Signal } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import type { LinkValueInterface } from '~/types/AppData/Layout/LinkValueInterface';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { ConditionalWrapper } from '~/components/Shared/Utility/ConditionalWrapper/ConditionalWrapper';

const navLink = cva('', {
  variants: {
    isTopLevel: {
      true: 'mobile-nav-top',
      false: 'mobile-nav-sub',
    },
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
  },
});

const navWrap = cva('list-none mb-5 leading-5', {
  variants: {
    width: {
      half: 'w-1/2',
      full: 'w-full',
    },
  },
});

type MobileNavLinkType = {
  item: LinkValueInterface;
  mobileActive?: Signal<boolean>;
  level: number;
};

export default component$(
  ({ item, level, mobileActive }: MobileNavLinkType): JSX.Element => {
    const appData = useContext(AppDataContext);

    // Link text classes are inherited from the parent for subnavs, but needed for top level links
    // We do this to minimize the classes that are added to each link element
    const linkClass =
      level === 0
        ? navLink({
            isTopLevel: true,
            color:
              appData.themeComponents.header.hamburgerMenuLinksNormalColor
                .value,
            ...getFontSizeObject(
              appData.themeComponents.header,
              'hamburgerMenuLinksFontSize'
            ),
          })
        : navLink({
            isTopLevel: false,
            color:
              appData.themeComponents.header.hamburgerMenuLinksNormalColor
                .value,
            ...getFontSizeObject(
              appData.themeComponents.header,
              'hamburgerMenuSubLinksFontSize'
            ),
          });

    if (
      item.link.url.includes(appData.payloadToSSR.canonicalURL) &&
      item.link.url.includes('#')
    ) {
      item.link.url = '#' + item.link.url.split('#')[1];
    }

    return (
      <li class={navWrap({ width: item.width ? item.width : 'full' })}>
        <ConditionalWrapper component='h4' condition={level === 0}>
          <a
            href={item.link.url}
            onClick$={() => {
              // Close the mobile menu when an anchor link is clicked
              if (mobileActive && item.link.url.includes('#')) {
                mobileActive.value = false;
              }
            }}
            class={linkClass}
          >
            {item.link.text}
          </a>
        </ConditionalWrapper>
      </li>
    );
  }
);
